// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Box, Button, Header, SpaceBetween, Table } from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import { RetryAlert } from '../../Common/ErrorMessages';
import { convertDateTimePretty, valueOrDash } from '../../../utils/ReadableConverter';
import { ApiError } from '../../../utils/serverErrors';
import * as terGroups from './terGroups';

interface TerGroupsListProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
  };
  navigate: (path: string) => void;
  canWrite: boolean;
}

const columnDefinitions = [
  {
    id: 'groupName',
    header: 'Group name',
    cell: (group: terGroups.TerGroupSummary): JSX.Element => (
      <Link to={terGroups.terGroupRoute(group.terGroup)}>{group.groupName}</Link>
    ),
  },
  ...terGroups.METADATA_FIELDS.map(({ field, label }) => ({
    id: field,
    header: label,
    cell: (group: terGroups.TerGroupSummary): string => valueOrDash(group[field]),
  })),
  {
    id: 'version',
    header: 'Version',
    cell: (group: terGroups.TerGroupSummary): number => group.version,
  },
  {
    id: 'lastChanged',
    header: 'Last changed',
    cell: (group: terGroups.TerGroupSummary): string =>
      `${convertDateTimePretty(group.createdAt)} by ${group.author}`,
  },
];

export const TerGroupsList = ({
  apiClient,
  navigate,
  canWrite,
}: TerGroupsListProps): JSX.Element => {
  const [groups, setGroups] = useState<terGroups.TerGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);

  const load = useCallback((): void => {
    setLoading(true);
    setLoadErrors([]);
    apiClient
      .httpGet(terGroups.TER_GROUPS_PATH)
      .then(response => setGroups(response as terGroups.TerGroupSummary[]))
      .catch((error: ApiError) => {
        setGroups([]);
        setLoadErrors(terGroups.describeError(error));
      })
      .finally(() => setLoading(false));
  }, [apiClient]);

  useEffect(load, [load]);

  const createPath = `${terGroups.TER_GROUPS_ROUTE}/new`;

  return (
    <SpaceBetween size="m">
      {loadErrors.length > 0 && (
        <RetryAlert
          id="terGroupsLoadError"
          header="Could not load TER groups"
          messages={loadErrors}
          retryId="retryTerGroups"
          onRetry={load}
        />
      )}
      <Table
        id="terGroupsTable"
        items={groups}
        loading={loading}
        loadingText="Loading TER groups"
        trackBy="terGroup"
        columnDefinitions={columnDefinitions}
        header={
          <Header
            variant="h2"
            actions={
              canWrite && (
                <Button
                  id="createTerGroup"
                  variant="primary"
                  href={createPath}
                  onFollow={(event): void => {
                    event.preventDefault();
                    navigate(createPath);
                  }}
                >
                  Create TER group
                </Button>
              )
            }
          >
            TER groups
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            {loadErrors.length > 0 ? '' : 'No TER groups.'}
          </Box>
        }
      />
    </SpaceBetween>
  );
};

TerGroupsList.propTypes = {
  apiClient: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
};

const TerGroupsListView = (props: Omit<TerGroupsListProps, 'apiClient'>): JSX.Element => (
  <TerGroupsList apiClient={ApiClient} {...props} />
);

export default TerGroupsListView;
